import cn from 'clsx';
import { Fragment, ReactNode, useEffect, useRef } from 'react';
import IconButton from '@v2/shared/components/IconButton';
import { Dialog, Transition } from '@headlessui/react';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { faAngleLeft } from '@fortawesome/pro-regular-svg-icons';
import { useSite } from '@v2/shared/lib/site';

export interface FullScreenDialogProps {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
  title: string;
  showBackButton?: boolean;
  onBack?: () => void;
  slideInFrom: 'left' | 'right';
  /**
   * Hide the panel when it closes rather than tearing it down, so that its subtree
   * survives being closed and re-opened.
   *
   * Off by default, and it should stay off for anything whose content is cheap to draw:
   * a mounted dialog keeps its children — and their effects, and their timers — alive
   * for as long as the page is open. Turn it on where re-mounting costs something the
   * customer would notice. The cart drawer does, because rebuilding it re-runs the three
   * payment sdks from nothing.
   *
   * It is only the *unmounting* that this changes. HeadlessUI still reads the dialog as
   * closed — no scroll lock, no focus trap, no `aria-hidden` on the rest of the page, no
   * escape handler — and `display: none` keeps the panel out of the accessibility tree
   * and out of the tab order, so a hidden dialog is inert in every sense but the literal.
   */
  keepMounted?: boolean;
  children?: ReactNode;
}

export default function FullScreenDialog({
  isOpen,
  close,
  title,
  showBackButton = false,
  onBack,
  slideInFrom = 'left',
  keepMounted = false,
  children,
}: FullScreenDialogProps) {
  const closeButtonRef = useRef(null);
  const { brand } = useSite();

  /*
   * HeadlessUI's render strategy, and it has to be given to all four of these.
   *
   * `Transition`, `Transition.Child` and `Dialog` each decide independently what to do
   * when they are not visible, and each one defaults to unmounting — so a single missed
   * `unmount` anywhere down the chain still empties the panel and the ones above it are
   * left holding nothing. Told `false`, they render `hidden style="display:none"` and
   * keep their children instead.
   */
  const unmount = !keepMounted;

  /*
   * A kept panel still opens at the top, which is the one thing keeping it costs.
   *
   * A panel that is torn down comes back with a fresh scroll container at zero, so
   * nobody had to arrange that. A kept one comes back exactly where it was left — a
   * cart scrolled to its last line, closed and re-opened by an add-to-cart, would
   * re-open scrolled past the line that was just added. This is the dialog's own
   * problem to solve, because it is the dialog that decided not to unmount.
   */
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  return (
    <Transition show={isOpen} as={Fragment} unmount={unmount}>
      {/*
        v2-root here is not decoration: HeadlessUI renders a Dialog through a portal
        appended to document.body, so this subtree is mounted *outside* whatever
        element the component itself was rendered into.
        On a V2 page that is harmless — the scope class sits on <html>, so the portal
        is still inside it. On a legacy page the scope is the mount wrapper, and the
        portal escapes it: every rule in the V2 stylesheet is written as a descendant
        of .v2-root (see src/DMagzWebV2/postcss/scopeV2.js), so the dialog would open
        completely unstyled. Carrying the class on the Dialog re-establishes the scope
        wherever the portal happens to land. Nesting it inside another .v2-root is
        harmless; the selectors match either way.

        data-brand has to come with it, and not only so the brand is inherited. The
        brand palettes are custom properties declared *on the scope class* -- .v2-root
        sets the DiscountMags values and .v2-root[data-brand='topmags'] overrides them
        (styles/v2.css). So a portal carrying the class without the attribute does not
        merely fail to pick the brand up: it re-declares the base palette on itself and
        actively resets an inherited one. On TopMags that turned every themed colour
        inside a dialog back to DiscountMags red.
      */}
      <Dialog className="v2-root" data-brand={brand} onClose={close} initialFocus={closeButtonRef} unmount={unmount}>
        <Transition.Child
          as={Fragment}
          unmount={unmount}
          enter="transition duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          {/* Backdrop */}
          <div className={cn('fixed inset-0 bg-slate-900/80 z-[260]')} aria-hidden="true" />
        </Transition.Child>

        {/*
          The panel is anchored to its edge and animated by its own width, rather than
          parked at the left and pushed across the viewport.

          It used to translate by `calc(100vw - 384px)`, and that is a 15px bug on every
          machine with a visible scrollbar: `100vw` includes the scrollbar, while the
          containing block of a `fixed` element does not, so the panel came to rest 15px
          past the right edge with that much of it clipped off-screen. Worse to look at
          than to describe -- opening the dialog locks the page scroll, which removes the
          scrollbar and changes what the viewport measures *while the animation is
          running*, so the panel visibly jumped sideways on its way in.

          `translate-x-full` is a percentage of the element, not of the viewport, so
          neither the scrollbar nor the scroll lock can move it. Off-screen is exactly
          one panel width away whatever the window is doing.
        */}
        <Transition.Child
          as={Fragment}
          unmount={unmount}
          enter="transition ease duration-200"
          enterFrom={slideInFrom === 'left' ? '-translate-x-full' : 'translate-x-full'}
          enterTo="translate-x-0"
          leave="transition ease-out duration-200"
          leaveFrom="translate-x-0"
          leaveTo={slideInFrom === 'left' ? '-translate-x-full' : 'translate-x-full'}
        >
          {/* Panel */}
          <Dialog.Panel
            className={cn(
              'fixed inset-y-0 w-full md:w-96 bg-white z-[270]',
              slideInFrom === 'left' ? 'left-0' : 'right-0'
            )}
          >
            <Dialog.Title
              className={cn(
                'p-2 bg-white flex justify-between items-center border-b-[1.5px] border-slate-200 absolute top-0 inset-x-0 z-[290]',
                {
                  'pl-4': !showBackButton,
                }
              )}
            >
              {showBackButton && onBack && <IconButton icon={faAngleLeft} onClick={onBack} ariaLabel="Back" />}
              <span className="font-semibold text-xl">{title}</span>
              <IconButton icon={faTimes} onClick={close} ariaLabel="Close" ref={closeButtonRef} />
            </Dialog.Title>
            <div
              ref={scrollRef}
              className="overflow-y-scroll absolute inset-x-0 top-0 bottom-0 z-[280] mt-[53.5px]"
            >
              {children}
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
}
